import { Link, useSearchParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/members/pagination'
import { useAuth } from '@/hooks/use-auth'
import { useArrearsAgingSummary, useMemberArrears } from '@/hooks/use-billing'
import { exportMemberArrears } from '@/lib/billing'
import { getApiErrorMessage } from '@/lib/api'
import { downloadCsv } from '@/lib/csv'
import { cn, formatKES } from '@/lib/utils'
import type { ArrearsBucket, MemberArrears } from '@riverpark/types'

/** Where this role's member profile lives — each staff role has its own member-detail route. */
function memberProfilePath(role: string | undefined, memberId: string): string {
  const prefix =
    role === 'committee' ? '/committee' : role === 'estate_manager' ? '/operations' : '/admin'
  return `${prefix}/members/${memberId}?tab=billing`
}

const BUCKETS: { id: ArrearsBucket; label: string }[] = [
  { id: 'current', label: 'Current' },
  { id: 'days1to30', label: '1–30 days' },
  { id: 'days31to60', label: '31–60 days' },
  { id: 'days61to90', label: '61–90 days' },
  { id: 'days90Plus', label: '90+ days' },
]

function isArrearsBucket(value: string): value is ArrearsBucket {
  return (BUCKETS as { id: string }[]).some((b) => b.id === value)
}

const PAGE_SIZE = 20

function downloadArrearsCsv(rows: MemberArrears[]) {
  downloadCsv(
    `riverpark-arrears-${new Date().toISOString().slice(0, 10)}.csv`,
    [
      'Member',
      'Phone',
      'Outstanding (KES)',
      'Unpaid invoices',
      'Current',
      '1-30 days',
      '31-60 days',
      '61-90 days',
      '90+ days',
    ],
    rows.map((r) => [
      r.memberName,
      r.memberPhone,
      r.outstandingAmount,
      r.invoiceCount,
      r.aging.current,
      r.aging.days1to30,
      r.aging.days31to60,
      r.aging.days61to90,
      r.aging.days90Plus,
    ]),
  )
}

export function ArrearsReportTab() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  // Supports deep-linking here (e.g. from the Payments Overview), prefiltered to one bucket.
  const requestedBucket = searchParams.get('bucket')
  const [page, setPage] = useState(1)
  const [bucket, setBucket] = useState<ArrearsBucket | null>(
    requestedBucket && isArrearsBucket(requestedBucket) ? requestedBucket : null,
  )
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const { data, isLoading } = useMemberArrears({
    page,
    limit: PAGE_SIZE,
    ...(bucket && { bucket }),
  })
  const rows = data?.data ?? []
  const meta = data?.meta ?? { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 }
  const { data: agingData } = useArrearsAgingSummary()
  const agingSummary = agingData?.data

  function selectBucket(id: ArrearsBucket) {
    setBucket((current) => (current === id ? null : id))
    setPage(1)
  }

  async function handleExport() {
    setExportError(null)
    setExporting(true)
    try {
      // Full dataset regardless of the on-screen page — pagination is a display concern only.
      const { data: allRows } = await exportMemberArrears(bucket ?? undefined)
      downloadArrearsCsv(allRows)
    } catch (err) {
      setExportError(getApiErrorMessage(err, 'Failed to export arrears'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {BUCKETS.map(({ id, label }) => {
          const entry = agingSummary?.[id]
          const active = bucket === id
          return (
            <button
              key={id}
              type="button"
              onClick={() => selectBucket(id)}
              className={cn(
                'rounded-card border px-3 py-2.5 text-left transition-colors',
                active
                  ? 'border-primary bg-primary/5'
                  : 'border-border-2 bg-card hover:border-ink/30',
              )}
            >
              <p className="font-sans text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                {label}
              </p>
              <p className="mt-1 font-serif text-lg leading-none tracking-tight text-ink">
                {entry ? formatKES(entry.total) : '—'}
              </p>
              <p className="mt-1 font-sans text-[11px] text-muted-foreground">
                {entry ? `${entry.memberCount} member(s)` : ''}
              </p>
            </button>
          )
        })}
      </div>

      <div className="flex items-center justify-between">
        <p className="font-sans text-sm text-muted-foreground">
          {bucket
            ? `Members with a balance in the "${BUCKETS.find((b) => b.id === bucket)?.label}" bucket. Click again to clear.`
            : 'Members with any unpaid or overdue invoice, ranked by outstanding balance.'}
        </p>
        <Button
          variant="secondary"
          size="sm"
          disabled={meta.total === 0}
          loading={exporting}
          onClick={() => void handleExport()}
        >
          <Download size={14} className="mr-1.5" />
          Export CSV
        </Button>
      </div>
      {exportError && (
        <p className="font-sans text-xs text-destructive" role="alert">
          {exportError}
        </p>
      )}

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No outstanding balances.
        </p>
      ) : (
        <>
          {/* Mobile */}
          <div className="space-y-2 sm:hidden">
            {rows.map((r) => (
              <Link
                key={r.memberId}
                to={memberProfilePath(user?.role, r.memberId)}
                className="block rounded border border-border-2 bg-card p-3"
              >
                <span className="font-sans text-sm font-medium text-ink">{r.memberName}</span>
                <p className="mt-1 font-sans text-xs text-muted-foreground">
                  {r.memberPhone} · {r.invoiceCount} unpaid invoice(s) ·{' '}
                  <span className="font-medium text-destructive">
                    {formatKES(r.outstandingAmount)}
                  </span>
                </p>
              </Link>
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden overflow-x-auto rounded border border-border-2 sm:block">
            <table className="w-full font-sans text-sm">
              <thead>
                <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Unpaid invoices</th>
                  <th className="px-4 py-3">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.memberId} className="border-b border-border-2 last:border-0">
                    <td className="px-4 py-3 font-medium text-ink">
                      <Link
                        to={memberProfilePath(user?.role, r.memberId)}
                        className="text-ink hover:text-primary hover:underline"
                      >
                        {r.memberName}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-ink">{r.memberPhone}</td>
                    <td className="px-4 py-3 text-ink">{r.invoiceCount}</td>
                    <td className="px-4 py-3 font-medium text-destructive">
                      {formatKES(r.outstandingAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {meta.total > 0 && (
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          onPageChange={setPage}
          emptyLabel="No outstanding balances"
          className="rounded border border-border-2 bg-card"
        />
      )}
    </div>
  )
}
